import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToolaSyncService } from './toola-sync.service';
import { ToolaSyncStatusComponent } from './toola-sync-status.component';

/**
 * Reports > Toola Sync. Headline counts (the status card) + a date/store
 * reconciliation table: sales vs posted-to-Toola per store per day, so the user
 * can cross-check totals. Expand any row to see the bill-level breakup (which
 * posted, which didn't, and why).
 */
@Component({
  selector: 'app-toola-sync-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ToolaSyncStatusComponent],
  template: `
    <div class="container-fluid py-3">
      <h5 class="mb-3">Toola Accounting Sync</h5>

      <app-toola-sync-status [clientId]="clientId"></app-toola-sync-status>

      <div class="card">
        <div class="card-body">
          <div class="d-flex gap-2 align-items-end mb-3 flex-wrap">
            <div>
              <label class="form-label small mb-0">From</label>
              <input type="date" class="form-control form-control-sm" [(ngModel)]="from" />
            </div>
            <div>
              <label class="form-label small mb-0">To</label>
              <input type="date" class="form-control form-control-sm" [(ngModel)]="to" />
            </div>
            <div>
              <label class="form-label small mb-0">Store</label>
              <select class="form-select form-select-sm" [(ngModel)]="storeFilter">
                <option value="">All stores</option>
                <option *ngFor="let s of stores" [value]="s">{{ s }}</option>
              </select>
            </div>
            <button class="btn btn-sm btn-primary" (click)="loadRecon()">Load</button>
            <div class="ms-auto text-danger small" *ngIf="reachable === false">
              Sync connector unreachable.
            </div>
          </div>

          <div *ngIf="loading" class="text-muted small">Loading…</div>

          <div class="table-responsive" *ngIf="!loading">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th style="width:32px"></th>
                  <th>Date</th>
                  <th>Store</th>
                  <th class="text-end">Sales (bills / ₹)</th>
                  <th class="text-end">Posted (bills / ₹)</th>
                  <th class="text-center">Pending</th>
                  <th class="text-center">Failed</th>
                  <th class="text-center">Match</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let r of filteredRows">
                  <tr>
                    <td>
                      <button class="btn btn-sm btn-link p-0 text-decoration-none" (click)="toggle(r)">
                        {{ isOpen(r) ? '▾' : '▸' }}
                      </button>
                    </td>
                    <td>{{ r.bizDate }}</td>
                    <td>{{ r.storeCode || r.storeId }}</td>
                    <td class="text-end">{{ r.salesCount }} / ₹{{ r.salesAmount | number: '1.2-2' }}</td>
                    <td class="text-end">{{ r.postedCount }} / ₹{{ r.postedAmount | number: '1.2-2' }}</td>
                    <td class="text-center text-warning">{{ r.pendingCount || 0 }}</td>
                    <td class="text-center" [class.text-danger]="r.failedCount > 0">{{ r.failedCount || 0 }}</td>
                    <td class="text-center">
                      <span [class.text-success]="r.matched" [class.text-danger]="!r.matched">
                        {{ r.matched ? '✓' : '✗' }}
                      </span>
                    </td>
                  </tr>
                  <tr *ngIf="isOpen(r)">
                    <td></td>
                    <td colspan="7">
                      <div class="text-muted small mb-1">
                        Bills for {{ r.storeCode || r.storeId }} on {{ r.bizDate }}
                      </div>
                      <table class="table table-sm mb-0">
                        <thead><tr><th>Bill No</th><th>Amount</th><th>Status</th><th>Toola / Error</th></tr></thead>
                        <tbody>
                          <tr *ngFor="let it of drill[keyOf(r)]">
                            <td>{{ it.billNo || (it.sourceRef | slice: 0:8) }}</td>
                            <td>{{ it.amount != null ? '₹' + it.amount : '—' }}</td>
                            <td><span [ngClass]="statusColor(it.status)">{{ statusLabel(it.status) }}</span></td>
                            <td>
                              <span *ngIf="it.status === 'DONE'" class="text-muted small">Toola #{{ it.toolaDocId }}</span>
                              <span *ngIf="it.status !== 'DONE'" class="text-danger small">{{ it.lastError }}</span>
                            </td>
                          </tr>
                          <tr *ngIf="!(drill[keyOf(r)] && drill[keyOf(r)].length)">
                            <td colspan="4" class="text-muted">no bills</td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </ng-container>
                <tr *ngIf="!filteredRows.length && !loading">
                  <td colspan="8" class="text-muted text-center py-3">no data for this range</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class ToolaSyncPageComponent implements OnInit {
  /** Wire to the logged-in user's Client UUID for per-business scoping. */
  clientId?: string;

  from = '';
  to = '';
  storeFilter = '';
  rows: any[] = [];
  loading = false;
  reachable: boolean | null = null;
  open = new Set<string>();
  drill: Record<string, any[]> = {};

  constructor(private svc: ToolaSyncService) {}

  get stores(): string[] {
    const set = new Set<string>();
    for (const r of this.rows) set.add(r.storeCode || r.storeId);
    return Array.from(set).sort();
  }

  get filteredRows(): any[] {
    if (!this.storeFilter) return this.rows;
    return this.rows.filter(
      (r) => (r.storeCode || r.storeId) === this.storeFilter,
    );
  }

  ngOnInit(): void {
    const today = new Date();
    const week = new Date();
    week.setDate(week.getDate() - 6);
    this.to = today.toISOString().slice(0, 10);
    this.from = week.toISOString().slice(0, 10);
    this.loadRecon();
  }

  keyOf(r: any): string {
    return (r.storeId || '') + '#' + r.bizDate;
  }

  loadRecon(): void {
    this.loading = true;
    this.open.clear();
    this.drill = {};
    this.svc.getRecon(this.from, this.to, this.clientId).subscribe({
      next: (res) => {
        this.rows = res?.rows || [];
        this.reachable = res?.reachable !== false;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.reachable = false;
      },
    });
  }

  isOpen(r: any): boolean {
    return this.open.has(this.keyOf(r));
  }

  toggle(r: any): void {
    const k = this.keyOf(r);
    if (this.open.has(k)) {
      this.open.delete(k);
      return;
    }
    this.open.add(k);
    if (!this.drill[k]) {
      this.svc.getItems(undefined, this.clientId, r.storeId, r.bizDate).subscribe({
        next: (res) => (this.drill[k] = res?.items || []),
        error: () => (this.drill[k] = []),
      });
    }
  }

  statusColor(s: string): string {
    if (s === 'DONE') return 'text-success';
    if (s === 'PENDING' || s === 'PROCESSING') return 'text-warning';
    return 'text-danger';
  }

  statusLabel(s: string): string {
    return s === 'DONE' ? 'Posted ✓' : s;
  }
}
